// The shared verse tally.
//
// Reads are batched: one request per verse would be wasteful and would make the
// app depend on the network for something purely decorative. Verses read pile
// up locally and are flushed on a timer, and again when the page is hidden, so
// a reader who closes the tab still contributes.
//
// Everything here fails silently. A missing or unreachable backend means the
// counter is not shown; it never interferes with reading.

const PENDING_KEY = 'quranReaderPendingVerses';
const FLUSH_MS = 30000;

let timer = null;

const pending = () => {
  try {
    return Math.max(0, parseInt(localStorage.getItem(PENDING_KEY), 10) || 0);
  } catch (error) {
    return 0;
  }
};

const setPending = (value) => {
  try {
    if (value > 0) localStorage.setItem(PENDING_KEY, String(value));
    else localStorage.removeItem(PENDING_KEY);
  } catch (error) {
    // Private mode, quota: the tally is not worth failing over
  }
};

export const fetchCommunityTotal = async () => {
  try {
    const response = await fetch('/api/stats');
    if (!response.ok) return null;
    const { total } = await response.json();
    return Number.isFinite(total) ? total : null;
  } catch (error) {
    return null;
  }
};

export const flushPending = async () => {
  const amount = pending();
  if (amount <= 0) return null;

  // Cleared up front so a flush arriving twice cannot double count. If the
  // request fails the count is put back.
  setPending(0);
  try {
    const response = await fetch('/api/stats', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verses: amount }),
      keepalive: true          // survives the page being closed
    });
    if (!response.ok) throw new Error(`stats ${response.status}`);
    const { total } = await response.json();
    return Number.isFinite(total) ? total : null;
  } catch (error) {
    setPending(pending() + amount);
    return null;
  }
};

export const recordVerseForCommunity = () => {
  setPending(pending() + 1);
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flushPending();
  }, FLUSH_MS);
};

// A reader who closes the tab mid-session should still be counted
export const installFlushOnHide = () => {
  const onHide = () => { if (document.visibilityState === 'hidden') flushPending(); };
  document.addEventListener('visibilitychange', onHide);
  return () => document.removeEventListener('visibilitychange', onHide);
};
